import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  hostCreateProperty,
  hostGetPartialPaymentPolicy,
  hostGetProperty,
  hostGetPropertyPartialPayment,
  hostSubmitProperty,
  hostSaveImages,
  hostUpdateAmenities,
  hostUpdatePropertyPartialPayment,
  hostUpdatePricingRules,
  hostUpdateProperty,
} from '../api/host';
import { uploadHostDraftImage, uploadHostPropertyImage } from '../services/propertyImagesApi';
import { validateImageFile, hashFile } from '../components/GalleryUploader';
import { apiGet } from '../services/apiClient';
import { formatPaymentBreakdown } from '../utils/partialPayment';

const steps = [
  { key: 'basic', label: 'Basic Details' },
  { key: 'location', label: 'Location' },
  { key: 'rooms', label: 'Rooms & Capacity' },
  { key: 'amenities', label: 'Amenities' },
  { key: 'media', label: 'Images & Media' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'availability', label: 'Availability & Calendar' },
  { key: 'policies', label: 'Policies & Rules' },
  { key: 'addons', label: 'Add-ons' },
  { key: 'seo', label: 'SEO / Listing Visibility' },
];

const propertyTypeOptions = ['Apartment', 'Villa', 'Homestay', 'Hotel', 'Hostel', 'Guest House', 'Resort', 'Farm stay'];
const bookingModeOptions = [
  { value: 'full_property', label: 'Entire property (full booking)' },
  { value: 'room', label: 'Room-wise (multi-room)' },
];
const currencyOptions = ['INR', 'USD', 'EUR', 'GBP'];
const amenityOptions = [
  { key: 'wifi', label: 'WiFi' },
  { key: 'ac', label: 'Air conditioning' },
  { key: 'heater', label: 'Room heater' },
  { key: 'bbq_grill', label: 'BBQ grill' },
  { key: 'bonfire', label: 'Bonfire setup' },
  { key: 'garden', label: 'Private garden' },
  { key: 'private_pool', label: 'Private pool' },
  { key: 'power_backup', label: 'Power backup' },
  { key: 'refrigerator', label: 'Refrigerator' },
  { key: 'washing_machine', label: 'Washer / Dryer' },
  { key: 'free_parking', label: 'Parking' },
  { key: 'pet_friendly', label: 'Pet friendly' },
  { key: 'smoking_allowed', label: 'Smoking allowed' },
  { key: 'security_cameras', label: 'Security cameras' },
];

const HOST_QUICK_FILL_STORAGE_KEY = 'homev.host.quickFillPolicies';
const HOST_QUICK_FILL_DEFAULTS = {
  cancellation_policy: [
    { label: 'Flexible 24h (full refund 24h prior)', value: 'Full refund if cancelled 24h before check-in; 50% after.' },
    { label: 'Moderate 3-day', value: 'Full refund up to 3 days before check-in; 50% after.' },
    { label: 'Strict 7-day', value: '50% refund up to 7 days before check-in; no refund after.' },
  ],
  refund_rules: [
    { label: 'Standard refund', value: 'Refunds processed within 7 business days of approval.' },
    { label: 'No refunds after check-in', value: 'No refunds after check-in except for documented issues.' },
  ],
  pet_policy: [
    { label: 'Pets not allowed', value: 'Pets are not allowed.' },
    { label: 'Pets allowed with fee', value: 'Pets allowed on approval; cleaning fee may apply.' },
  ],
  smoking_policy: [
    {
      label: 'No smoking or alcohol indoors',
      value: 'Smoking and alcohol consumption are not allowed indoors. Designated outdoor areas only.',
    },
    {
      label: 'Outdoor only',
      value: 'Smoking and alcohol consumption allowed outdoors only; indoor areas are smoke-free.',
    },
  ],
  guest_profile: [
    { label: 'Families & couples preferred', value: 'Suitable for families and couples. Bachelor groups only with prior approval.' },
    { label: 'All guests welcome', value: 'All guests are welcome. Please respect neighbors and quiet hours.' },
  ],
  id_requirement: [
    { label: 'Government ID required', value: 'Valid government-issued ID required for all guests at check-in.' },
    { label: 'Primary guest ID only', value: 'Primary guest must present a valid government ID at check-in.' },
  ],
};

const HOUSE_RULES_TITLES = {
  guestProfile: 'Guest Profile',
  idProof: 'ID Proof Related',
  pet: 'Pet(s) Related',
  smoking: 'Smoking & Alcohol Consumption Rules',
  smokingLegacy: 'Smoking/Alcohol consumption Rules',
};

const defaultHouseRulesSections = [
  { title: 'House Rules & Information', items: [] },
  { title: 'Must Read Rules', items: [] },
  { title: HOUSE_RULES_TITLES.guestProfile, items: [] },
  { title: HOUSE_RULES_TITLES.idProof, items: [] },
  { title: HOUSE_RULES_TITLES.smoking, items: [] },
  { title: 'Property Accessibility', items: [] },
  { title: HOUSE_RULES_TITLES.pet, items: [] },
  { title: 'Finding keys to the property', items: [] },
  { title: 'Other Rules', items: [] },
];

const cloneHouseRulesSections = (sections = defaultHouseRulesSections) =>
  sections.map((section) => ({
    title: section?.title || '',
    items: Array.isArray(section?.items) ? [...section.items] : [],
  }));

const normalizeSectionTitle = (title) =>
  String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const resolveHouseRulesTitle = (title) => {
  const normalized = normalizeSectionTitle(title);
  if (normalized === normalizeSectionTitle(HOUSE_RULES_TITLES.smokingLegacy)) {
    return HOUSE_RULES_TITLES.smoking;
  }
  return title;
};

const normalizeQuickFillOption = (option) => {
  if (!option) return null;
  const label = String(option.label || option.name || '').trim();
  const value = String(option.value || option.content || option.text || '').trim();
  if (!value) return null;
  return { label: label || value.slice(0, 48), value };
};

const mergeQuickFillList = (base = [], extra = []) => {
  const merged = [...base, ...extra].map(normalizeQuickFillOption).filter(Boolean);
  const seen = new Set();
  return merged.filter((item) => {
    const key = `${item.label.toLowerCase()}|${item.value.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mergeQuickFillOptions = (defaults, stored = {}) => {
  const next = {};
  Object.keys(defaults).forEach((key) => {
    next[key] = mergeQuickFillList(defaults[key], stored[key] || []);
  });
  return next;
};

const loadQuickFillOptions = (defaults, storageKey) => {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const stored = JSON.parse(raw);
    return mergeQuickFillOptions(defaults, stored);
  } catch {
    return defaults;
  }
};

const persistQuickFillOptions = (storageKey, options) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(options));
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
};

const normalizeTextList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (!value) return [];
  return String(value)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
};

const normalizeHouseRulesSections = (value, fallback) => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return normalizeHouseRulesSections(parsed, fallback);
    } catch {
      // fall back to legacy rules
    }
  }
  if (Array.isArray(value)) {
    const cleaned = value
      .map((section) => {
        if (!section) return null;
        const rawTitle = String(section.title || section.label || section.name || '').trim();
        const title = resolveHouseRulesTitle(rawTitle);
        const items = normalizeTextList(section.items || section.rules || section.content || section.text || section.notes);
        if (!title && !items.length) return null;
        return { title: title || 'House Rules', items };
      })
      .filter(Boolean);
    if (cleaned.length) return cleaned;
  }
  const fallbackItems = normalizeTextList(fallback);
  if (fallbackItems.length) {
    return [{ title: 'House Rules & Information', items: fallbackItems }];
  }
  return cloneHouseRulesSections();
};

const buildHouseRulesSectionsPayload = (sections) => {
  const list = Array.isArray(sections) ? sections : [];
  return list
    .map((section, idx) => {
      if (!section) return null;
      const rawTitle = String(section.title || '').trim();
      const title = resolveHouseRulesTitle(rawTitle);
      const items = normalizeTextList(section.items || section.rules || section.content || section.text || section.notes);
      if (!title && !items.length) return null;
      return { title: title || `Section ${idx + 1}`, items };
    })
    .filter(Boolean);
};

const findHouseRulesSectionIndex = (sections, title, aliases = []) => {
  const normalizedTargets = [title, ...aliases].map(normalizeSectionTitle);
  return sections.findIndex((section) => normalizedTargets.includes(normalizeSectionTitle(section?.title || '')));
};

const getHouseRulesSectionText = (sections, title, aliases = []) => {
  if (!Array.isArray(sections)) return '';
  const matchIndex = findHouseRulesSectionIndex(sections, title, aliases);
  if (matchIndex < 0) return '';
  const items = Array.isArray(sections[matchIndex]?.items) ? sections[matchIndex].items : [];
  return items
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .join('\n');
};

const mergeHouseRulesSectionsPayload = (sections, { petPolicy, smokingPolicy, idRequirement, guestProfile } = {}) => {
  const list = (Array.isArray(sections) ? sections : []).map((section) => ({
    title: resolveHouseRulesTitle(String(section?.title || '').trim()),
    items: Array.isArray(section?.items) ? [...section.items] : [],
  }));
  const apply = (title, value, aliases = []) => {
    const items = normalizeTextList(value);
    if (!items.length) return;
    const matchIndex = findHouseRulesSectionIndex(list, title, aliases);
    if (matchIndex >= 0) {
      const existing = list[matchIndex];
      const mergedItems = Array.isArray(existing.items) ? [...existing.items] : [];
      items.forEach((item) => {
        if (!mergedItems.some((entry) => String(entry).toLowerCase() === String(item).toLowerCase())) {
          mergedItems.push(item);
        }
      });
      list[matchIndex] = { ...existing, title, items: mergedItems };
    } else {
      list.push({ title, items: [...items] });
    }
  };
  apply(HOUSE_RULES_TITLES.pet, petPolicy);
  apply(HOUSE_RULES_TITLES.smoking, smokingPolicy, [HOUSE_RULES_TITLES.smokingLegacy]);
  apply(HOUSE_RULES_TITLES.idProof, idRequirement);
  apply(HOUSE_RULES_TITLES.guestProfile, guestProfile);
  return list;
};

const hasHouseRulesContent = (sections, fallback, extra = []) => {
  const payload = buildHouseRulesSectionsPayload(sections);
  if (payload.some((section) => Array.isArray(section.items) && section.items.length)) return true;
  if (normalizeTextList(fallback).length > 0) return true;
  return extra.some((value) => normalizeTextList(value).length > 0);
};

const initialForm = {
  title: '',
  property_code: '',
  property_type: '',
  description: '',
  short_description: '',
  star_rating: '',
  status: 'draft',
  video_url: '',
  featured_image: '',
  country: '',
  state: '',
  city: '',
  area: '',
  address_line: '',
  latitude: '',
  longitude: '',
  bedrooms: 1,
  beds: 1,
  bathrooms: 1,
  max_guests: 2,
  check_in_time: '12:00 PM',
  check_out_time: '10:00 AM',
  check_in_notes: '',
  check_out_notes: '',
  base_weekday_price: '',
  base_weekend_price: '',
  weekly_discount: '',
  monthly_discount: '',
  extra_guest_charge: '',
  cleaning_fee: '',
  service_fee: '',
  security_deposit: '',
  taxes_percent: '',
  currency: 'INR',
  min_nights: 1,
  max_nights: 30,
  booking_window_days: 180,
  cutoff_hours: 24,
  allow_same_day_booking: true,
  weekend_pricing_type: 'percent',
  weekend_pricing_value: 0,
  booking_mode: 'full_property',
  cancellation_policy: '',
  house_rules: '',
  house_rules_sections: cloneHouseRulesSections(),
  guest_profile: '',
  id_requirement: '',
  pet_policy: '',
  smoking_policy: '',
  refund_rules: '',
  security_deposit_policy: '',
  meal_details: '',
  faq_items: [{ question: '', answer: '' }],
  meta_title: '',
  meta_description: '',
  slug: '',
  amenities_custom: '',
};

const createInitialFormState = () => ({
  ...initialForm,
  house_rules_sections: cloneHouseRulesSections(),
  faq_items: [{ question: '', answer: '' }],
});

const createInitialAddonsState = () => [{ name: '', description: '', price: '', per: 'booking' }];

const initialAddons = [{ name: '', description: '', price: '', per: 'booking' }];
const emptyRoomType = {
  name: '',
  description: '',
  capacity: 2,
  bed_type: '',
  inventory: 1,
  base_price_per_night: '',
  weekend_price_per_night: '',
  extra_guest_charge: '',
  currency: 'INR',
  is_active: true,
};
const emptyImageRow = { url: '', alt_text: '', is_cover: false };

const Input = ({ label, required, error, helper, ...props }) => (
  <label className="space-y-1 text-sm text-slate-700">
    <div className="flex items-center gap-1 font-semibold">
      <span>{label}</span>
      {required && <span className="text-rose-600">*</span>}
    </div>
    <input
      {...props}
      className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
        error ? 'border-rose-300' : 'border-slate-200'
      }`}
    />
    {helper && <p className="text-xs text-slate-500">{helper}</p>}
    {error && <p className="text-xs text-rose-600">{error}</p>}
  </label>
);

const Select = ({ label, required, error, options, helper, ...props }) => (
  <label className="space-y-1 text-sm text-slate-700">
    <div className="flex items-center gap-1 font-semibold">
      <span>{label}</span>
      {required && <span className="text-rose-600">*</span>}
    </div>
    <select
      {...props}
      className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
        error ? 'border-rose-300' : 'border-slate-200'
      }`}
    >
      <option value="">Select</option>
      {options.map((opt) => {
        const value = typeof opt === 'string' ? opt : opt.value;
        const labelText = typeof opt === 'string' ? opt : opt.label;
        return (
          <option key={value} value={value}>
            {labelText}
          </option>
        );
      })}
    </select>
    {helper && <p className="text-xs text-slate-500">{helper}</p>}
    {error && <p className="text-xs text-rose-600">{error}</p>}
  </label>
);

const TextArea = ({ label, required, error, helper, rows = 3, ...props }) => (
  <label className="space-y-1 text-sm text-slate-700">
    <div className="flex items-center gap-1 font-semibold">
      <span>{label}</span>
      {required && <span className="text-rose-600">*</span>}
    </div>
    <textarea
      {...props}
      rows={rows}
      className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
        error ? 'border-rose-300' : 'border-slate-200'
      }`}
    />
    {helper && <p className="text-xs text-slate-500">{helper}</p>}
    {error && <p className="text-xs text-rose-600">{error}</p>}
  </label>
);

const StepPill = ({ index, label, active, complete, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(index)}
    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
      active ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
    }`}
  >
    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-[11px] text-white">{index + 1}</span>
    <span>{label}</span>
    {complete && <span className="text-emerald-600">✓</span>}
  </button>
);

const toNumber = (val, fallback = null) => {
  if (val === '' || val === null || val === undefined) return fallback;
  const num = Number(val);
  return Number.isFinite(num) ? num : fallback;
};

const trimOrNull = (val) => {
  const str = (val ?? '').toString().trim();
  return str ? str : null;
};

const safeTrim = (val) => (val ?? '').toString().trim();

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const resolveImageUrl = (url = '') => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE}${normalized}`;
};

const eq = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

const stepFields = {
  basic: [
    'title',
    'property_type',
    'description',
    'short_description',
    'property_code',
    'star_rating',
    'check_in_notes',
    'check_out_notes',
    'id_requirement',
    'security_deposit_policy',
    'meal_details',
    'faq_items',
  ],
  location: ['country', 'state', 'city', 'area', 'address_line', 'latitude', 'longitude'],
  rooms: ['bedrooms', 'beds', 'bathrooms', 'max_guests', 'check_in_time', 'check_out_time', 'room_types', 'booking_mode'],
  amenities: ['amenities', 'amenities_custom'],
  media: ['images', 'video_url'],
  pricing: [
    'base_weekday_price',
    'base_weekend_price',
    'currency',
    'taxes_percent',
    'security_deposit',
    'cleaning_fee',
    'extra_guest_charge',
    'service_fee',
    'weekly_discount',
    'monthly_discount',
  ],
  availability: [
    'min_nights',
    'max_nights',
    'booking_window_days',
    'cutoff_hours',
    'allow_same_day_booking',
    'weekend_pricing_type',
    'weekend_pricing_value',
  ],
  policies: [
    'cancellation_policy',
    'pet_policy',
    'smoking_policy',
    'guest_profile',
    'refund_rules',
    'house_rules',
    'house_rules_sections',
  ],
  addons: ['addons'],
  seo: ['meta_title', 'meta_description', 'slug', 'status'],
};

const getAmenityKeysFromRow = (row) =>
  Object.entries(row || {})
    .filter(([key, value]) => typeof value === 'boolean' && value && key !== 'id' && key !== 'property_id')
    .map(([key]) => key);

const validateStep = (
  key,
  form,
  images,
  amenities,
  addons,
  publishIntent = false,
  selectedCountryId = '',
  selectedStateId = '',
  selectedCityId = '',
  roomTypes = []
) => {
  const errs = {};
  const requireNumber = (field, label) => {
    if (form[field] === '' || form[field] === null || form[field] === undefined) {
      errs[field] = `${label} is required`;
      return;
    }
    if (!Number.isFinite(Number(form[field]))) {
      errs[field] = `${label} must be a number`;
    }
  };

  switch (key) {
    case 'basic':
      if (!safeTrim(form.title)) errs.title = 'Property name is required';
      if (!safeTrim(form.property_type)) errs.property_type = 'Property type is required';
      if (!safeTrim(form.description)) errs.description = 'Description is required';
      if (safeTrim(form.description).length > 0 && safeTrim(form.description).length < 10)
        errs.description = 'Description must be at least 10 characters';
      if (form.star_rating && (Number(form.star_rating) < 0 || Number(form.star_rating) > 5)) {
        errs.star_rating = 'Star rating must be between 0 and 5';
      }
      break;
    case 'location':
      if (!selectedCountryId || !safeTrim(form.country)) errs.country = 'Select a country';
      if (!selectedStateId || !safeTrim(form.state)) errs.state = 'Select a state';
      if (!selectedCityId || !safeTrim(form.city)) errs.city = 'Select a city';
      if (!safeTrim(form.address_line)) errs.address_line = 'Address is required';
      if (form.latitude && Number.isNaN(Number(form.latitude))) errs.latitude = 'Latitude must be a number';
      if (form.longitude && Number.isNaN(Number(form.longitude))) errs.longitude = 'Longitude must be a number';
      if (form.latitude !== '' && form.latitude !== null && form.latitude !== undefined) {
        const lat = Number(form.latitude);
        if (Number.isFinite(lat) && (lat < -90 || lat > 90)) {
          errs.latitude = 'Latitude must be between -90 and 90';
        }
      }
      if (form.longitude !== '' && form.longitude !== null && form.longitude !== undefined) {
        const lng = Number(form.longitude);
        if (Number.isFinite(lng) && (lng < -180 || lng > 180)) {
          errs.longitude = 'Longitude must be between -180 and 180';
        }
      }
      break;
    case 'rooms': {
      requireNumber('max_guests', 'Max guests');
      requireNumber('bedrooms', 'Bedrooms');
      requireNumber('bathrooms', 'Bathrooms');
      if (form.beds !== '' && form.beds !== null && form.beds !== undefined && Number(form.beds) < 0) {
        errs.beds = 'Beds cannot be negative';
      }
      if (!form.check_in_time) errs.check_in_time = 'Check-in time is required';
      if (!form.check_out_time) errs.check_out_time = 'Check-out time is required';
      const roomMode = String(form.booking_mode || '').toLowerCase() === 'room';
      if (roomMode) {
        if (!Array.isArray(roomTypes) || roomTypes.length === 0) {
          errs.room_types = 'Add at least one room type for room-wise bookings.';
        } else {
          const hasInvalidRoom = roomTypes.some(
            (room) => !safeTrim(room?.name) || room?.base_price_per_night === '' || Number.isNaN(Number(room?.base_price_per_night))
          );
          if (hasInvalidRoom) {
            errs.room_types = 'Add room name and base price for each room type.';
          }
        }
      }
      break;
    }
    case 'amenities':
      if (!amenities.length && !safeTrim(form.amenities_custom)) {
        errs.amenities = 'Select at least one amenity or add a custom amenity';
      }
      break;
    case 'media':
      {
        const validImages = images.filter((img) => img.url.trim());
        if (validImages.length < 4) {
          errs.images = 'Add at least 4 images';
        }
      }
      break;
    case 'pricing':
      requireNumber('base_weekday_price', 'Base price (weekday)');
      requireNumber('base_weekend_price', 'Weekend price');
      if (!form.currency) errs.currency = 'Currency is required';
      if (form.min_nights && form.max_nights && Number(form.max_nights) < Number(form.min_nights)) {
        errs.max_nights = 'Maximum stay must be greater than or equal to minimum stay';
      }
      ['taxes_percent', 'security_deposit', 'cleaning_fee', 'extra_guest_charge', 'service_fee', 'weekly_discount', 'monthly_discount'].forEach(
        (field) => {
          if (form[field] !== '' && form[field] !== null && form[field] !== undefined && Number.isNaN(Number(form[field]))) {
            errs[field] = 'Must be a number';
          }
        }
      );
      break;
    case 'availability':
      if (form.booking_window_days !== '' && Number(form.booking_window_days) < 0) {
        errs.booking_window_days = 'Booking window cannot be negative';
      }
      if (form.cutoff_hours !== '' && Number(form.cutoff_hours) < 0) {
        errs.cutoff_hours = 'Advance notice cannot be negative';
      }
      if (form.weekend_pricing_value !== '' && Number.isNaN(Number(form.weekend_pricing_value))) {
        errs.weekend_pricing_value = 'Value must be numeric';
      }
      break;
    case 'policies':
      if (!safeTrim(form.cancellation_policy)) errs.cancellation_policy = 'Cancellation policy is required';
      if (
        publishIntent &&
        !hasHouseRulesContent(form.house_rules_sections, form.house_rules, [
          form.pet_policy,
          form.smoking_policy,
          form.id_requirement,
          form.guest_profile,
        ])
      ) {
        errs.house_rules = 'Add at least one house rule before publishing';
      }
      break;
    case 'addons':
      addons.forEach((addon, idx) => {
        const rowKey = `addons-${idx}`;
        if ((addon.name || addon.price || addon.description) && addon.price !== '' && Number.isNaN(Number(addon.price))) {
          errs[rowKey] = 'Add-on price must be numeric';
        }
      });
      break;
    case 'seo':
      if (publishIntent && !safeTrim(form.meta_title)) errs.meta_title = 'Meta title is required for publishing';
      if (publishIntent && !safeTrim(form.meta_description)) errs.meta_description = 'Meta description is required for publishing';
      break;
    default:
      break;
  }
  return errs;
};

const HostPropertyForm = ({ accessToken }) => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [createdPropertyId, setCreatedPropertyId] = useState(null);
  const [form, setForm] = useState(() => createInitialFormState());
  const [images, setImages] = useState([{ ...emptyImageRow, is_cover: true }]);
  const [amenities, setAmenities] = useState([]);
  const [addons, setAddons] = useState(() => createInitialAddonsState());
  const [roomTypes, setRoomTypes] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [quickFillOptions, setQuickFillOptions] = useState(() =>
    loadQuickFillOptions(HOST_QUICK_FILL_DEFAULTS, HOST_QUICK_FILL_STORAGE_KEY)
  );
  const [quickFillDrafts, setQuickFillDrafts] = useState({});
  const [countries, setCountries] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [selectedStateId, setSelectedStateId] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [locationLoading, setLocationLoading] = useState({ countries: false, states: false, cities: false });
  const [amenityDropdownOpen, setAmenityDropdownOpen] = useState(false);
  const [extraAmenityOptions, setExtraAmenityOptions] = useState([]);
  const [newAmenityOption, setNewAmenityOption] = useState('');
  const [amenityOptionError, setAmenityOptionError] = useState('');
  const [aboutTab, setAboutTab] = useState('checkin');
  const [policiesTab, setPoliciesTab] = useState('policies');
  const [bulkImagesText, setBulkImagesText] = useState('');
  const [fileImportError, setFileImportError] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingMessage, setUploadingMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [partialPaymentPolicy, setPartialPaymentPolicy] = useState(null);
  const [partialPaymentForm, setPartialPaymentForm] = useState({
    partialPaymentEnabled: false,
    partialPaymentPercentage: '',
  });
  const [partialPaymentLoading, setPartialPaymentLoading] = useState(false);
  const fileInputRef = useRef(null);
  const activePropertyId = id || createdPropertyId;

  useEffect(() => {
    if (isEdit) return;
    setCreatedPropertyId(null);
    setForm(createInitialFormState());
    setImages([{ ...emptyImageRow, is_cover: true }]);
    setAmenities([]);
    setAddons(createInitialAddonsState());
    setRoomTypes([]);
    setCurrentStep(0);
    setErrors({});
    setStatusMessage('');
    setSelectedCountryId('');
    setSelectedStateId('');
    setSelectedCityId('');
    setStatesList([]);
    setCitiesList([]);
    setAmenityDropdownOpen(false);
    setExtraAmenityOptions([]);
    setNewAmenityOption('');
    setAmenityOptionError('');
    setAboutTab('checkin');
    setPoliciesTab('policies');
    setBulkImagesText('');
    setFileImportError('');
    setUploadingMessage('');
    setUploadError('');
    setPartialPaymentPolicy(null);
    setPartialPaymentForm({
      partialPaymentEnabled: false,
      partialPaymentPercentage: '',
    });
  }, [isEdit, id]);

  const allAmenityOptions = useMemo(
    () => [...amenityOptions, ...extraAmenityOptions],
    [extraAmenityOptions]
  );

  const hydratePartialPaymentForm = (policy) => {
    const hostSetting = policy?.hostSetting || null;
    const effective = policy?.effectiveConfig || {};
    const globalPolicy = policy?.adminPolicy?.global || {};
    const nextPercentage =
      hostSetting?.partialPaymentPercentage ??
      effective?.percentage ??
      globalPolicy?.defaultPartialPaymentPercentage ??
      globalPolicy?.minimumPartialPaymentPercentage ??
      '';
    const nextEnabled = hostSetting?.partialPaymentEnabled ?? Boolean(effective?.isPartialPaymentAllowed);
    setPartialPaymentForm({
      partialPaymentEnabled: Boolean(nextEnabled),
      partialPaymentPercentage:
        nextPercentage === null || nextPercentage === undefined || nextPercentage === '' ? '' : String(nextPercentage),
    });
  };

  const partialPaymentRange = useMemo(() => {
    const globalPolicy = partialPaymentPolicy?.adminPolicy?.global || {};
    return {
      min: Number(globalPolicy.minimumPartialPaymentPercentage ?? 1),
      max: Number(globalPolicy.maximumPartialPaymentPercentage ?? 100),
    };
  }, [partialPaymentPolicy]);

  const partialPaymentHostCanEdit = Boolean(partialPaymentPolicy?.effectiveConfig?.hostCanEdit);
  const partialPaymentPreview = useMemo(() => {
    if (!partialPaymentForm.partialPaymentEnabled) return null;
    const totalAmount = Number(form.base_weekday_price || 0);
    const percentage = Number(partialPaymentForm.partialPaymentPercentage || 0);
    if (!Number.isFinite(totalAmount) || totalAmount <= 0 || !Number.isFinite(percentage) || percentage <= 0) {
      return null;
    }
    try {
      return formatPaymentBreakdown({
        totalAmount,
        paymentOptionSelected: 'partial',
        partialPaymentPercentage: percentage,
      });
    } catch (_) {
      return null;
    }
  }, [form.base_weekday_price, partialPaymentForm.partialPaymentEnabled, partialPaymentForm.partialPaymentPercentage]);

  const loadCountries = async () => {
    setLocationLoading((prev) => ({ ...prev, countries: true }));
    try {
      const res = await apiGet('/api/public/locations/countries');
      setCountries(res || []);
    } catch (_) {
      setCountries([]);
    } finally {
      setLocationLoading((prev) => ({ ...prev, countries: false }));
    }
  };

  const loadStates = async (countryId) => {
    if (!countryId) {
      setStatesList([]);
      setCitiesList([]);
      return;
    }
    setLocationLoading((prev) => ({ ...prev, states: true }));
    try {
      const res = await apiGet(`/api/public/locations/states?countryId=${countryId}`);
      setStatesList(res || []);
    } catch (_) {
      setStatesList([]);
    } finally {
      setLocationLoading((prev) => ({ ...prev, states: false }));
    }
  };

  const loadCities = async (stateId) => {
    if (!stateId) {
      setCitiesList([]);
      return;
    }
    setLocationLoading((prev) => ({ ...prev, cities: true }));
    try {
      const res = await apiGet(`/api/public/locations/cities?stateId=${stateId}`);
      setCitiesList(res || []);
    } catch (_) {
      setCitiesList([]);
    } finally {
      setLocationLoading((prev) => ({ ...prev, cities: false }));
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      setLoading(true);
      try {
        const data = await hostGetProperty(accessToken, id);
        const normalizedFaqItems = Array.isArray(data?.faq_items)
          ? data.faq_items
              .map((item) => ({
                question: item?.question || item?.q || '',
                answer: item?.answer || item?.a || '',
              }))
              .filter((item) => item.question || item.answer)
          : [];
        const amenityKeys = getAmenityKeysFromRow(data.amenities);
        const gallery = Array.isArray(data.images)
          ? data.images.map((img) => ({
              url: img.image_url || img.url || '',
              alt_text: img.alt_text || '',
              is_cover: !!img.is_cover,
            }))
          : [];
        const normalizedHouseRulesSections = normalizeHouseRulesSections(data?.house_rules_sections, data?.house_rules);
        const guestProfileText = getHouseRulesSectionText(
          normalizedHouseRulesSections,
          HOUSE_RULES_TITLES.guestProfile
        );
        setForm((prev) => ({
          ...prev,
          ...initialForm,
          ...data,
          check_in_notes: data.check_in_notes || '',
          check_out_notes: data.check_out_notes || '',
          security_deposit_policy: data.security_deposit_policy || '',
          meal_details: data.meal_details || '',
          faq_items: normalizedFaqItems.length ? normalizedFaqItems : initialForm.faq_items,
          house_rules_sections: normalizedHouseRulesSections,
          guest_profile: guestProfileText,
          id_requirement: data.id_requirement || '',
          status: data.status || 'draft',
          currency: data.currency || prev.currency,
          booking_window_days: data.booking_window_days ?? prev.booking_window_days,
          cutoff_hours: data.cutoff_hours ?? prev.cutoff_hours,
        }));
        setAmenities(amenityKeys);
        setImages(gallery.length ? gallery : [{ ...emptyImageRow, is_cover: true }]);
        const parsedAddons =
          Array.isArray(data.addons) && data.addons.length
            ? data.addons.map((a) => ({
                name: a.name || a.title || a.label || '',
                description: a.description || a.notes || '',
                price: a.price ?? a.amount ?? '',
                per: a.per || a.unit || 'booking',
              }))
            : initialAddons;
        setAddons(parsedAddons);
        setRoomTypes(Array.isArray(data.room_types) ? data.room_types : []);
        const missingAmenityKeys = amenityKeys.filter(
          (key) => !amenityOptions.some((opt) => opt.key === key)
        );
        if (missingAmenityKeys.length) {
          setExtraAmenityOptions((prev) => {
            const knownKeys = new Set([...prev.map((a) => a.key), ...amenityOptions.map((a) => a.key)]);
            const additions = missingAmenityKeys
              .filter((key) => !knownKeys.has(key))
              .map((key) => ({
                key,
                label: key
                  .split('_')
                  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                  .join(' '),
              }));
            return additions.length ? [...prev, ...additions] : prev;
          });
        }
      } catch (err) {
        setErrors({ form: err.message || 'Failed to load property' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [accessToken, id, isEdit]);

  useEffect(() => {
    let active = true;
    const loadPartialPayment = async () => {
      if (!accessToken) return;
      setPartialPaymentLoading(true);
      try {
        const policy = activePropertyId
          ? await hostGetPropertyPartialPayment(accessToken, activePropertyId)
          : await hostGetPartialPaymentPolicy(accessToken);
        if (!active) return;
        setPartialPaymentPolicy(policy);
        hydratePartialPaymentForm(policy);
        setErrors((prev) => {
          const next = { ...prev };
          delete next.partial_payment;
          return next;
        });
      } catch (err) {
        if (!active) return;
        setPartialPaymentPolicy(null);
        setErrors((prev) => ({
          ...prev,
          partial_payment: err.message || 'Failed to load partial payment policy',
        }));
      } finally {
        if (active) setPartialPaymentLoading(false);
      }
    };
    loadPartialPayment();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, activePropertyId]);

  const stepCompletion = useMemo(
    () =>
      steps.map((step) => {
        const errs = validateStep(
          step.key,
          form,
          images,
          amenities,
          addons,
          false,
          selectedCountryId,
          selectedStateId,
          selectedCityId,
          roomTypes
        );
        return Object.keys(errs).length === 0;
      }),
    [form, images, amenities, addons, selectedCountryId, selectedStateId, selectedCityId, roomTypes]
  );

  const clearAmenityError = () =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next.amenities;
      return next;
    });

  useEffect(() => {
    persistQuickFillOptions(HOST_QUICK_FILL_STORAGE_KEY, quickFillOptions);
  }, [quickFillOptions]);

  const updateQuickFillDraft = (key, updates) =>
    setQuickFillDrafts((prev) => ({
      ...prev,
      [key]: { label: '', value: '', open: false, error: '', ...(prev[key] || {}), ...updates },
    }));

  const addQuickFillOption = (key) => {
    const draft = quickFillDrafts[key] || {};
    const label = String(draft.label || '').trim();
    const value = String(draft.value || '').trim();
    if (!value) {
      updateQuickFillDraft(key, { error: 'Enter quick fill text.' });
      return;
    }
    const nextOption = { label: label || value.slice(0, 48), value };
    setQuickFillOptions((prev) => ({
      ...prev,
      [key]: mergeQuickFillList(prev[key] || [], [nextOption]),
    }));
    updateQuickFillDraft(key, { label: '', value: '', open: false, error: '' });
  };

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    if (!selectedCountryId) {
      setStatesList([]);
      setCitiesList([]);
      return;
    }
    if (String(selectedCountryId).startsWith('custom-')) {
      if (form.state && !statesList.some((s) => eq(s.name, form.state))) {
        setStatesList((prev) => [...prev, { id: `custom-${form.state}`, name: form.state }]);
      }
      return;
    }
    loadStates(selectedCountryId);
  }, [selectedCountryId, form.state]);

  useEffect(() => {
    if (!selectedStateId) {
      setCitiesList([]);
      return;
    }
    if (String(selectedStateId).startsWith('custom-')) {
      if (form.city && !citiesList.some((c) => eq(c.name, form.city))) {
        setCitiesList((prev) => [...prev, { id: `custom-${form.city}`, name: form.city }]);
      }
      return;
    }
    loadCities(selectedStateId);
  }, [selectedStateId, form.city]);

  useEffect(() => {
    if (!selectedCountryId && countries.length && form.country) {
      const match = countries.find((c) => eq(c.name, form.country) || eq(c.code, form.country));
      if (match) {
        setSelectedCountryId(String(match.id));
      } else if (!countries.some((c) => eq(c.name, form.country))) {
        const synthetic = { id: `custom-${form.country}`, name: form.country };
        setCountries((prev) => [...prev, synthetic]);
        setSelectedCountryId(String(synthetic.id));
      }
    }
  }, [countries, form.country, selectedCountryId]);

  useEffect(() => {
    if (!selectedStateId && statesList.length && form.state) {
      const match = statesList.find((s) => eq(s.name, form.state) || eq(s.code, form.state));
      if (match) {
        setSelectedStateId(String(match.id));
      } else if (!statesList.some((s) => eq(s.name, form.state))) {
        const synthetic = { id: `custom-${form.state}`, name: form.state };
        setStatesList((prev) => [...prev, synthetic]);
        setSelectedStateId(String(synthetic.id));
      }
    }
  }, [statesList, form.state, selectedStateId]);

  useEffect(() => {
    if (!selectedCityId && citiesList.length && form.city) {
      const match = citiesList.find((c) => eq(c.name, form.city) || eq(c.code, form.city));
      if (match) {
        setSelectedCityId(String(match.id));
      } else if (!citiesList.some((c) => eq(c.name, form.city))) {
        const synthetic = { id: `custom-${form.city}`, name: form.city };
        setCitiesList((prev) => [...prev, synthetic]);
        setSelectedCityId(String(synthetic.id));
      }
    }
  }, [citiesList, form.city, selectedCityId]);

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      if (key in next) delete next[key];
      if (key === 'amenities_custom' && next.amenities) delete next.amenities;
      return next;
    });
  };

  const handleNumberField = (key, value) => {
    if (value === '') {
      handleFieldChange(key, '');
      return;
    }
    const num = Number(value);
    handleFieldChange(key, Number.isNaN(num) ? value : num);
  };

  const handleCountrySelect = (e) => {
    const id = e.target.value;
    setSelectedCountryId(id);
    const selected = countries.find((c) => String(c.id) === id);
    setForm((prev) => ({ ...prev, country: selected?.name || '', state: '', city: '' }));
    setSelectedStateId('');
    setSelectedCityId('');
    setStatesList([]);
    setCitiesList([]);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.country;
      delete next.state;
      delete next.city;
      return next;
    });
  };

  const handleStateSelect = (e) => {
    const id = e.target.value;
    setSelectedStateId(id);
    const selected = statesList.find((s) => String(s.id) === id);
    setForm((prev) => ({ ...prev, state: selected?.name || '', city: '' }));
    setSelectedCityId('');
    setCitiesList([]);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.state;
      delete next.city;
      return next;
    });
  };

  const handleCitySelect = (e) => {
    const id = e.target.value;
    setSelectedCityId(id);
    const selected = citiesList.find((c) => String(c.id) === id);
    setForm((prev) => ({ ...prev, city: selected?.name || '' }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.city;
      return next;
    });
  };

  const toggleAmenity = (key) => {
    setAmenities((prev) => {
      const exists = prev.includes(key);
      const next = exists ? prev.filter((k) => k !== key) : [...prev, key];
      return next;
    });
    clearAmenityError();
  };

  const addAmenityOption = () => {
    const name = newAmenityOption.trim();
    if (!name) {
      setAmenityOptionError('Amenity name is required');
      return;
    }
    const slug =
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || `amenity_${Date.now()}`;
    const exists = allAmenityOptions.some(
      (opt) => opt.key === slug || opt.label.toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      setAmenityOptionError('Amenity already exists in the list');
      return;
    }
    const entry = { key: slug, label: name };
    setExtraAmenityOptions((prev) => [...prev, entry]);
    setAmenities((prev) => [...prev, entry.key]);
    setNewAmenityOption('');
    setAmenityOptionError('');
    setAmenityDropdownOpen(true);
    clearAmenityError();
  };

  const updateImage = (idx, key, value) => {
    setImages((prev) => {
      const next = prev.map((img, i) => {
        if (i !== idx) return key === 'is_cover' && value ? { ...img, is_cover: false } : img;
        const updated = { ...img, [key]: value };
        if (key === 'is_cover' && value) {
          return { ...updated, is_cover: true };
        }
        return updated;
      });
      if (!next.some((img) => img.is_cover) && next.some((img) => img.url.trim())) {
        const firstWithUrl = next.findIndex((img) => img.url.trim());
        if (firstWithUrl >= 0) next[firstWithUrl] = { ...next[firstWithUrl], is_cover: true };
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.images;
      return next;
    });
  };

  const removeImageRow = (idx) => {
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (!next.length) return [{ ...emptyImageRow, is_cover: true }];
      if (!next.some((img) => img.is_cover) && next[0]) {
        next[0] = { ...next[0], is_cover: true };
      }
      return next;
    });
  };

  const mergeImageUrls = (urls = []) => {
    if (!urls.length) return;
    setImages((prev) => {
      const existing = new Set(prev.map((img) => img.url.trim()).filter(Boolean));
      const additions = urls
        .map((u) => (u || '').trim())
        .filter((url) => url && !existing.has(url))
        .map((url) => ({ ...emptyImageRow, url }));
      const merged = [...prev, ...additions];
      if (!merged.some((img) => img.is_cover) && merged.some((img) => img.url.trim())) {
        const firstWithUrl = merged.findIndex((img) => img.url.trim());
        if (firstWithUrl >= 0) merged[firstWithUrl] = { ...merged[firstWithUrl], is_cover: true };
      }
      return merged;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.images;
      return next;
    });
  };

  const addBulkImages = () => {
    const parts = bulkImagesText
      .split(/[\n,]/)
      .map((p) => p.trim())
      .filter(Boolean);
    mergeImageUrls(parts);
    setBulkImagesText('');
  };

  const handleLocalUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingImages(true);
    setUploadingMessage('Preparing images...');
    setUploadError('');
    setErrors((prev) => {
      const next = { ...prev };
      delete next.form;
      return next;
    });
    try {
      const fileArray = Array.from(files);
      const seen = new Set();
      const validFiles = [];
      const issues = [];

      for (const file of fileArray) {
        // eslint-disable-next-line no-await-in-loop
        const validation = await validateImageFile(file, seen);
        if (!validation.valid) {
          issues.push(`${file.name}: ${validation.errors.join(', ')}`);
          continue;
        }
        seen.add(validation.fingerprint);
        validFiles.push({ file, validation });
      }

      if (!validFiles.length) {
        setUploadError(issues.join(' ') || 'No valid images selected.');
        return;
      }

      for (let i = 0; i < validFiles.length; i += 1) {
        const { file, validation } = validFiles[i];
        setUploadingMessage(`Uploading ${i + 1} of ${validFiles.length}...`);
        try {
          const fileHash = await hashFile(file).catch(() => null);
          const uploaded = activePropertyId
            ? await uploadHostPropertyImage(activePropertyId, file, accessToken, {
                onProgress: (pct) => setUploadingMessage(`Uploading ${i + 1} of ${validFiles.length}... ${pct}%`),
                fileHash,
                width: validation.dims.width,
                height: validation.dims.height,
              })
            : await uploadHostDraftImage(file, accessToken, {
              onProgress: (pct) => setUploadingMessage(`Uploading ${i + 1} of ${validFiles.length}... ${pct}%`),
              fileHash,
              width: validation.dims.width,
              height: validation.dims.height,
            });
          const uploadedUrl = uploaded?.url || uploaded?.image_url || uploaded?.path || '';
          if (uploadedUrl) {
            mergeImageUrls([uploadedUrl]);
          } else {
            issues.push('Upload succeeded but no URL returned. Please try again.');
          }
        } catch (err) {
          issues.push(err.message || 'Upload failed');
        }
      }

      setUploadError(issues.join(' '));
    } catch (err) {
      setUploadError(err.message || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
      setUploadingMessage('');
    }
  };

  const handleFileImport = (file) => {
    if (!file) return;
    setFileImportError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result || '';
      const urls = String(text)
        .split(/[\s,]+/)
        .map((p) => p.trim())
        .filter((p) => /^https?:\/\//i.test(p));
      if (!urls.length) {
        setFileImportError('No URLs found in file. Provide a text/CSV file with URLs.');
        return;
      }
      mergeImageUrls(urls);
    };
    reader.onerror = () => setFileImportError('Failed to read file. Please try a text or CSV file.');
    if (file.type.includes('text') || file.type.includes('csv') || /\.csv$/i.test(file.name) || /\.txt$/i.test(file.name)) {
      reader.readAsText(file);
    } else {
      setFileImportError('Only text or CSV files are supported for URL import.');
    }
  };

  const updateAddon = (idx, key, value) =>
    setAddons((prev) => prev.map((addon, i) => (i === idx ? { ...addon, [key]: value } : addon)));

  const addAddonRow = () => setAddons((prev) => [...prev, { name: '', description: '', price: '', per: 'booking' }]);
  const removeAddonRow = (idx) =>
    setAddons((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));

  const updateRoomType = (idx, key, value) =>
    setRoomTypes((prev) => prev.map((room, i) => (i === idx ? { ...room, [key]: value } : room)));
  const addRoomTypeRow = () =>
    setRoomTypes((prev) => [...prev, { ...emptyRoomType, currency: form.currency || 'INR' }]);
  const removeRoomTypeRow = (idx) =>
    setRoomTypes((prev) => prev.filter((_, i) => i !== idx));

  const updateFaqItem = (idx, key, value) =>
    setForm((prev) => ({
      ...prev,
      faq_items: (prev.faq_items || []).map((item, i) => (i === idx ? { ...item, [key]: value } : item)),
    }));
  const addFaqItem = () =>
    setForm((prev) => ({
      ...prev,
      faq_items: [...(prev.faq_items || []), { question: '', answer: '' }],
    }));
  const removeFaqItem = (idx) =>
    setForm((prev) => ({
      ...prev,
      faq_items:
        (prev.faq_items || []).length === 1
          ? prev.faq_items
          : (prev.faq_items || []).filter((_, i) => i !== idx),
    }));

  const updateHouseRulesSection = (idx, key, value) =>
    setForm((prev) => {
      const sections = Array.isArray(prev.house_rules_sections) ? prev.house_rules_sections : cloneHouseRulesSections();
      const next = sections.map((section, i) => {
        if (i !== idx) return section;
        if (key === 'items') {
          return { ...section, items: normalizeTextList(value) };
        }
        return { ...section, [key]: value };
      });
      return { ...prev, house_rules_sections: next };
    });

  const addHouseRulesSection = () =>
    setForm((prev) => {
      const sections = Array.isArray(prev.house_rules_sections) ? prev.house_rules_sections : cloneHouseRulesSections();
      return { ...prev, house_rules_sections: [...sections, { title: '', items: [] }] };
    });

  const removeHouseRulesSection = (idx) =>
    setForm((prev) => {
      const sections = Array.isArray(prev.house_rules_sections) ? prev.house_rules_sections : cloneHouseRulesSections();
      if (sections.length <= 1) return prev;
      return { ...prev, house_rules_sections: sections.filter((_, i) => i !== idx) };
    });

  const renderHouseRulesSectionsEditor = (showError = false) => {
    const houseRulesSections = Array.isArray(form.house_rules_sections) && form.house_rules_sections.length
      ? form.house_rules_sections
      : cloneHouseRulesSections();
    return (
      <div className="space-y-4">
        {houseRulesSections.map((section, idx) => (
          <div key={`house-rule-${idx}`} className="rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <Input
                  label="Section heading"
                  value={section.title || ''}
                  onChange={(e) => updateHouseRulesSection(idx, 'title', e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeHouseRulesSection(idx)}
                disabled={houseRulesSections.length === 1}
                className="mt-6 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Remove
              </button>
            </div>
            <TextArea
              label="Rules (one per line)"
              rows={3}
              value={(section.items || []).join('\n')}
              onChange={(e) => updateHouseRulesSection(idx, 'items', e.target.value)}
              helper="Add one point per line."
            />
          </div>
        ))}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={addHouseRulesSection}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            + Add section
          </button>
          {showError && errors.house_rules && <p className="text-xs text-rose-600">{errors.house_rules}</p>}
        </div>
      </div>
    );
  };

  const nextStep = () => {
    const stepKey = steps[currentStep].key;
    const stepErrors = validateStep(
      stepKey,
      form,
      images,
      amenities,
      addons,
      false,
      selectedCountryId,
      selectedStateId,
      selectedCityId,
      roomTypes
    );
    if (Object.keys(stepErrors).length) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      return;
    }
    setErrors((prev) => {
      const cleaned = { ...prev };
      (stepFields[stepKey] || []).forEach((field) => {
        delete cleaned[field];
      });
      return cleaned;
    });
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const firstErrorStep = (errs) => {
    const keys = Object.keys(errs);
    const idx = steps.findIndex((step) => keys.some((key) => stepFields[step.key]?.includes(key) || key.startsWith(`${step.key}-`)));
    return idx >= 0 ? idx : 0;
  };

  const persistPartialPayment = async (propertyId) => {
    if (!propertyId || !accessToken) return null;

    if (!partialPaymentHostCanEdit) {
      const refreshed = await hostGetPropertyPartialPayment(accessToken, propertyId);
      setPartialPaymentPolicy(refreshed);
      hydratePartialPaymentForm(refreshed);
      return refreshed;
    }

    const nextEnabled = Boolean(partialPaymentForm.partialPaymentEnabled);
    const nextPercentage = Number(partialPaymentForm.partialPaymentPercentage || 0);

    if (nextEnabled) {
      if (!Number.isFinite(nextPercentage) || nextPercentage <= 0 || nextPercentage > 100) {
        throw new Error('Partial payment percentage must be between 1 and 100.');
      }
      if (nextPercentage < partialPaymentRange.min || nextPercentage > partialPaymentRange.max) {
        throw new Error(
          `Partial payment percentage must stay between ${partialPaymentRange.min}% and ${partialPaymentRange.max}%.`
        );
      }
    }

    await hostUpdatePropertyPartialPayment(accessToken, propertyId, {
      partialPaymentEnabled: nextEnabled,
      partialPaymentPercentage: nextEnabled ? nextPercentage : null,
    });

    const refreshed = await hostGetPropertyPartialPayment(accessToken, propertyId);
    setPartialPaymentPolicy(refreshed);
    hydratePartialPaymentForm(refreshed);
    return refreshed;
  };

  const handleSave = async (publish = false) => {
    const stepsToValidate = publish ? steps : steps.slice(0, currentStep + 1);
    const aggregatedErrors = stepsToValidate.reduce(
      (acc, step) => ({
        ...acc,
        ...validateStep(
          step.key,
          form,
          images,
          amenities,
          addons,
          publish,
          selectedCountryId,
          selectedStateId,
          selectedCityId,
          roomTypes
        ),
      }),
      {}
    );
    if (Object.keys(aggregatedErrors).length) {
      setErrors(aggregatedErrors);
      setCurrentStep(firstErrorStep(aggregatedErrors));
      return;
    }
    setSaving(true);
    setStatusMessage('');
    setErrors({});
    try {
      let propertyId = activePropertyId;
      let generatedSlug = '';
      let generatedPropertyCode = '';
      if (!propertyId) {
        const createPayload = {
          title: form.title,
          property_type: form.property_type,
          booking_mode: form.booking_mode || 'full_property',
          base_weekday_price: toNumber(form.base_weekday_price, 0),
          base_weekend_price: toNumber(form.base_weekend_price, 0),
          security_deposit: toNumber(form.security_deposit, 0),
          taxes_percent: toNumber(form.taxes_percent, 0),
          max_guests: toNumber(form.max_guests, 2),
          bedrooms: toNumber(form.bedrooms, 1),
          bathrooms: toNumber(form.bathrooms, 1),
          check_in_time: form.check_in_time,
          check_out_time: form.check_out_time,
          currency: form.currency || 'INR',
        };
        const created = await hostCreateProperty(accessToken, createPayload);
        propertyId = created.id;
        generatedSlug = safeTrim(created?.slug);
        generatedPropertyCode = safeTrim(created?.property_code);
        setCreatedPropertyId(created.id);
        setForm((prev) => ({
          ...prev,
          slug: created?.slug || prev.slug,
          property_code: created?.property_code || prev.property_code,
          status: created?.status || prev.status,
        }));
      }

      const nextStatus =
        publish
          ? 'pending'
          : isEdit && (form.status === 'published' || form.status === 'suspended' || form.status === 'pending')
          ? form.status
          : 'draft';

      const faqItemsPayload = Array.isArray(form.faq_items)
        ? form.faq_items
            .map((item) => ({
              question: String(item?.question || '').trim(),
              answer: String(item?.answer || '').trim(),
            }))
            .filter((item) => item.question || item.answer)
        : [];
      const { guest_profile: guestProfile, ...formPayload } = form;
      const baseHouseRulesSections = buildHouseRulesSectionsPayload(formPayload.house_rules_sections);
      const houseRulesSectionsPayload = mergeHouseRulesSectionsPayload(baseHouseRulesSections, {
        petPolicy: formPayload.pet_policy,
        smokingPolicy: formPayload.smoking_policy,
        idRequirement: formPayload.id_requirement,
        guestProfile,
      });
      const houseRulesText = houseRulesSectionsPayload
        .flatMap((section) => section.items || [])
        .map((rule) => String(rule || '').trim())
        .filter(Boolean)
        .join('\n') || trimOrNull(formPayload.house_rules);

      const trimmedSlug = generatedSlug || safeTrim(form.slug);
      const trimmedPropertyCode = generatedPropertyCode || safeTrim(form.property_code);
      const payload = {
        ...formPayload,
        status: nextStatus,
        base_weekday_price: toNumber(form.base_weekday_price, 0),
        base_weekend_price: toNumber(form.base_weekend_price, 0),
        base_price_per_night: toNumber(form.base_weekday_price, 0),
        weekend_price_per_night: toNumber(form.base_weekend_price, 0),
        weekly_discount: toNumber(form.weekly_discount, null),
        monthly_discount: toNumber(form.monthly_discount, null),
        extra_guest_charge: toNumber(form.extra_guest_charge, null),
        cleaning_fee: toNumber(form.cleaning_fee, null),
        service_fee: toNumber(form.service_fee, null),
        security_deposit: toNumber(form.security_deposit, 0),
        taxes_percent: toNumber(form.taxes_percent, 0),
        max_guests: toNumber(form.max_guests, null),
        bedrooms: toNumber(form.bedrooms, null),
        beds: toNumber(form.beds, null),
        bathrooms: toNumber(form.bathrooms, null),
        min_nights: toNumber(form.min_nights, null),
        max_nights: toNumber(form.max_nights, null),
        latitude: toNumber(form.latitude, null),
        longitude: toNumber(form.longitude, null),
        star_rating: toNumber(form.star_rating, null),
        description: trimOrNull(form.description),
        short_description: trimOrNull(form.short_description),
        house_rules: houseRulesText || null,
        house_rules_sections: houseRulesSectionsPayload.length ? houseRulesSectionsPayload : null,
        check_in_notes: trimOrNull(form.check_in_notes),
        check_out_notes: trimOrNull(form.check_out_notes),
        id_requirement: trimOrNull(form.id_requirement),
        pet_policy: trimOrNull(form.pet_policy),
        smoking_policy: trimOrNull(form.smoking_policy),
        refund_rules: trimOrNull(form.refund_rules),
        security_deposit_policy: trimOrNull(form.security_deposit_policy),
        meal_details: trimOrNull(form.meal_details),
        faq_items: faqItemsPayload.length ? faqItemsPayload : null,
        meta_title: trimOrNull(form.meta_title),
        meta_description: trimOrNull(form.meta_description),
        amenities_custom: trimOrNull(form.amenities_custom),
        video_url: trimOrNull(form.video_url),
        featured_image: trimOrNull(form.featured_image),
        addons: addons
          .filter((a) => a.name || a.price || a.description)
          .map((a) => ({
            name: a.name,
            description: a.description,
            price: toNumber(a.price, null),
            per: a.per || 'booking',
          })),
        room_types: roomTypes.map((room, idx) => ({
          id: room.id,
          name: room.name,
          description: room.description,
          capacity: toNumber(room.capacity, 2),
          bed_type: room.bed_type,
          inventory: toNumber(room.inventory, 1),
          base_price_per_night: toNumber(room.base_price_per_night, 0),
          weekend_price_per_night: toNumber(room.weekend_price_per_night, toNumber(room.base_price_per_night, 0)),
          extra_guest_charge: toNumber(room.extra_guest_charge, 0),
          currency: room.currency || form.currency || 'INR',
          is_active: room.is_active !== false,
          sort_order: toNumber(room.sort_order, idx + 1),
        })),
      };
      if (trimmedSlug) payload.slug = trimmedSlug;
      if (trimmedPropertyCode) payload.property_code = trimmedPropertyCode;

      await hostUpdateProperty(accessToken, propertyId, payload);

      const weekendPricingType =
        form.weekend_pricing_type === 'flat' ? 'fixed' : form.weekend_pricing_type;
      await hostUpdatePricingRules(accessToken, propertyId, {
        min_nights: toNumber(form.min_nights, null),
        max_nights: toNumber(form.max_nights, null),
        booking_window_days: toNumber(form.booking_window_days, null),
        cutoff_hours: toNumber(form.cutoff_hours, null),
        weekend_pricing_type: weekendPricingType || 'percent',
        weekend_pricing_value: toNumber(form.weekend_pricing_value, 0),
        allow_same_day_booking: !!form.allow_same_day_booking,
        checkin_time: form.check_in_time,
        checkout_time: form.check_out_time,
      });

      if (amenities.length || form.amenities_custom.trim()) {
        await hostUpdateAmenities(accessToken, propertyId, amenities, trimOrNull(form.amenities_custom));
      }

      const imageRows = images
        .map((img, idx) => ({
          url: img.url.trim(),
          alt_text: img.alt_text || '',
          is_cover: !!img.is_cover,
          sort_order: idx + 1,
        }))
        .filter((img) => img.url);
      if (imageRows.length && !imageRows.some((img) => img.is_cover)) {
        imageRows[0].is_cover = true;
      }
      if (imageRows.length) {
        await hostSaveImages(accessToken, propertyId, imageRows);
      }
      if (!payload.featured_image) {
        const cover = imageRows.find((img) => img.is_cover) || imageRows[0];
        if (cover) {
          await hostUpdateProperty(accessToken, propertyId, { featured_image: cover.url });
          setForm((prev) => ({ ...prev, featured_image: cover.url }));
        }
      }

      await persistPartialPayment(propertyId);

      if (publish) {
        await hostSubmitProperty(accessToken, propertyId);
      }

      const finalStatus = publish ? 'pending' : nextStatus;
      setForm((prev) => ({ ...prev, status: finalStatus }));
      setStatusMessage(publish ? 'Submitted for approval' : 'Saved successfully (Draft)');
      if (publish) {
        navigate('/host/properties');
      }
    } catch (err) {
      setErrors({ form: err.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const savePoliciesTab = async (tabKey) => {
    if (!activePropertyId) {
      await handleSave(false);
      return;
    }
    setSaving(true);
    setStatusMessage('');
    setErrors({});
    try {
      const guestProfile = form.guest_profile;
      const baseHouseRulesSections = buildHouseRulesSectionsPayload(form.house_rules_sections);
      const houseRulesSectionsPayload = mergeHouseRulesSectionsPayload(baseHouseRulesSections, {
        petPolicy: form.pet_policy,
        smokingPolicy: form.smoking_policy,
        idRequirement: form.id_requirement,
        guestProfile,
      });
      const houseRulesText = houseRulesSectionsPayload
        .flatMap((section) => section.items || [])
        .map((rule) => String(rule || '').trim())
        .filter(Boolean)
        .join('\n') || trimOrNull(form.house_rules);
      const payload =
        tabKey === 'policies'
          ? {
              cancellation_policy: trimOrNull(form.cancellation_policy),
              refund_rules: trimOrNull(form.refund_rules),
            }
          : {
              pet_policy: trimOrNull(form.pet_policy),
              smoking_policy: trimOrNull(form.smoking_policy),
              id_requirement: trimOrNull(form.id_requirement),
              house_rules: houseRulesText || null,
              house_rules_sections: houseRulesSectionsPayload.length ? houseRulesSectionsPayload : null,
            };
      await hostUpdateProperty(accessToken, activePropertyId, payload);
      setStatusMessage(tabKey === 'policies' ? 'Policies saved.' : 'House rules saved.');
    } catch (err) {
      setErrors({ form: err.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const aboutTabs = [
    { key: 'checkin', label: 'Check-in' },
    { key: 'villa_rules', label: 'Villa Rules' },
    { key: 'security', label: 'Security Deposit' },
    { key: 'meals', label: 'Meals' },
    { key: 'faqs', label: 'FAQs' },
  ];

  const stepContent = () => {
    const faqItems = Array.isArray(form.faq_items) && form.faq_items.length
      ? form.faq_items
      : [{ question: '', answer: '' }];
    switch (steps[currentStep].key) {
      case 'basic':
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Property Name"
              required
              value={form.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              error={errors.title}
            />
            <Input
              label="Property Code"
              value={form.property_code}
              onChange={(e) => handleFieldChange('property_code', e.target.value)}
              helper="Leave blank to auto-generate"
              error={errors.property_code}
            />
            <Select
              label="Property Type"
              required
              value={form.property_type}
              onChange={(e) => handleFieldChange('property_type', e.target.value)}
              options={propertyTypeOptions}
              error={errors.property_type}
            />
            <Input
              label="Star rating"
              type="number"
              min="0"
              max="5"
              step="0.1"
              value={form.star_rating}
              onChange={(e) => handleNumberField('star_rating', e.target.value)}
              helper="0 - 5"
              error={errors.star_rating}
            />
            <TextArea
              label="Short description"
              rows={2}
              value={form.short_description}
              onChange={(e) => handleFieldChange('short_description', e.target.value)}
              helper="A one-liner shown in cards"
              error={errors.short_description}
            />
            <div className="md:col-span-2">
              <TextArea
                label="Description"
                required
                rows={4}
                value={form.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                error={errors.description}
              />
            </div>
            <div className="md:col-span-2 space-y-3 rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap gap-2">
                {aboutTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setAboutTab(tab.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      aboutTab === tab.key
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {aboutTab === 'checkin' && (
                <div className="grid gap-4 md:grid-cols-2">
                  <TextArea
                    label="Check-in notes"
                    rows={3}
                    value={form.check_in_notes || ''}
                    onChange={(e) => handleFieldChange('check_in_notes', e.target.value)}
                    helper="Add one point per line."
                  />
                  <TextArea
                    label="Check-out notes"
                    rows={3}
                    value={form.check_out_notes || ''}
                    onChange={(e) => handleFieldChange('check_out_notes', e.target.value)}
                    helper="Add one point per line."
                  />
                  <div className="md:col-span-2">
                    <Input
                      label="ID requirement"
                      value={form.id_requirement || ''}
                      onChange={(e) => handleFieldChange('id_requirement', e.target.value)}
                      helper="e.g., Please carry a valid government ID"
                    />
                  </div>
                </div>
              )}
              {aboutTab === 'villa_rules' && renderHouseRulesSectionsEditor(true)}
              {aboutTab === 'security' && (
                <TextArea
                  label="Security deposit notes"
                  rows={3}
                  value={form.security_deposit_policy || ''}
                  onChange={(e) => handleFieldChange('security_deposit_policy', e.target.value)}
                  helper="Deposit amount is set in Pricing."
                />
              )}
              {aboutTab === 'meals' && (
                <TextArea
                  label="Meals"
                  rows={3}
                  value={form.meal_details || ''}
                  onChange={(e) => handleFieldChange('meal_details', e.target.value)}
                  helper="Mention inclusions, timings, or add-ons."
                />
              )}
              {aboutTab === 'faqs' && (
                <div className="space-y-3">
                  {faqItems.map((item, idx) => (
                    <div key={`faq-${idx}`} className="rounded-lg border border-slate-200 p-3">
                      <div className="grid gap-3 md:grid-cols-2">
                        <Input
                          label={`Question ${idx + 1}`}
                          value={item.question || ''}
                          onChange={(e) => updateFaqItem(idx, 'question', e.target.value)}
                        />
                        <TextArea
                          label="Answer"
                          rows={2}
                          value={item.answer || ''}
                          onChange={(e) => updateFaqItem(idx, 'answer', e.target.value)}
                        />
                      </div>
                      <div className="mt-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeFaqItem(idx)}
                          className="text-xs font-semibold text-rose-600 hover:underline"
                          disabled={faqItems.length === 1}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFaqItem}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    + Add FAQ
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      case 'location':
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1 text-sm text-slate-700">
              <div className="flex items-center gap-1 font-semibold">
                <span>Country</span>
                <span className="text-rose-600">*</span>
              </div>
              <select
                value={selectedCountryId}
                onChange={handleCountrySelect}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  errors.country ? 'border-rose-300' : 'border-slate-200'
                }`}
              >
                <option value="">{locationLoading.countries ? 'Loading countries...' : 'Select country'}</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.title || c.code}
                  </option>
                ))}
              </select>
              {errors.country && <p className="text-xs text-rose-600">{errors.country}</p>}
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <div className="flex items-center gap-1 font-semibold">
                <span>State</span>
                <span className="text-rose-600">*</span>
              </div>
              <select
                value={selectedStateId}
                onChange={handleStateSelect}
                disabled={!selectedCountryId || locationLoading.states}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  errors.state ? 'border-rose-300' : 'border-slate-200'
                } ${!selectedCountryId ? 'bg-slate-50 text-slate-500' : ''}`}
              >
                <option value="">
                  {!selectedCountryId
                    ? 'Select country first'
                    : locationLoading.states
                    ? 'Loading states...'
                    : 'Select state'}
                </option>
                {statesList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.title || s.code}
                  </option>
                ))}
              </select>
              {errors.state && <p className="text-xs text-rose-600">{errors.state}</p>}
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <div className="flex items-center gap-1 font-semibold">
                <span>City</span>
                <span className="text-rose-600">*</span>
              </div>
              <select
                value={selectedCityId}
                onChange={handleCitySelect}
                disabled={!selectedStateId || locationLoading.cities}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  errors.city ? 'border-rose-300' : 'border-slate-200'
                } ${!selectedStateId ? 'bg-slate-50 text-slate-500' : ''}`}
              >
                <option value="">
                  {!selectedStateId
                    ? 'Select state first'
                    : locationLoading.cities
                    ? 'Loading cities...'
                    : 'Select city'}
                </option>
                {citiesList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.title || c.code}
                  </option>
                ))}
              </select>
              {errors.city && <p className="text-xs text-rose-600">{errors.city}</p>}
            </label>

            <Input label="Area / Locality" value={form.area} onChange={(e) => handleFieldChange('area', e.target.value)} error={errors.area} />
            <div className="md:col-span-2">
              <TextArea
                label="Full address"
                required
                value={form.address_line}
                onChange={(e) => handleFieldChange('address_line', e.target.value)}
                error={errors.address_line}
              />
            </div>
            <Input
              label="Latitude"
              value={form.latitude}
              onChange={(e) => handleFieldChange('latitude', e.target.value)}
              error={errors.latitude}
            />
            <Input
              label="Longitude"
              value={form.longitude}
              onChange={(e) => handleFieldChange('longitude', e.target.value)}
              error={errors.longitude}
            />
          </div>
        );
      case 'rooms':
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Max guests"
              required
              type="number"
              min="1"
              value={form.max_guests}
              onChange={(e) => handleNumberField('max_guests', e.target.value)}
              error={errors.max_guests}
            />
            <Input
              label="Bedrooms"
              required
              type="number"
              min="0"
              value={form.bedrooms}
              onChange={(e) => handleNumberField('bedrooms', e.target.value)}
              error={errors.bedrooms}
            />
            <Input
              label="Beds"
              type="number"
              min="0"
              value={form.beds}
              onChange={(e) => handleNumberField('beds', e.target.value)}
              error={errors.beds}
            />
            <Input
              label="Bathrooms"
              required
              type="number"
              min="0"
              value={form.bathrooms}
              onChange={(e) => handleNumberField('bathrooms', e.target.value)}
              error={errors.bathrooms}
            />
            <Input
              label="Check-in time"
              required
              value={form.check_in_time}
              onChange={(e) => handleFieldChange('check_in_time', e.target.value)}
              helper="e.g., 12:00 PM"
              error={errors.check_in_time}
            />
            <Input
              label="Check-out time"
              required
              value={form.check_out_time}
              onChange={(e) => handleFieldChange('check_out_time', e.target.value)}
              helper="e.g., 10:00 AM"
              error={errors.check_out_time}
            />
            <div className="md:col-span-2">
              <Select
                label="Booking type"
                required
                options={bookingModeOptions}
                value={form.booking_mode || 'full_property'}
                onChange={(e) => handleFieldChange('booking_mode', e.target.value)}
                helper="Choose whether guests book the entire property or select room types."
                error={errors.booking_mode}
              />
            </div>
            {String(form.booking_mode || 'full_property') === 'room' ? (
              <div className="md:col-span-2 space-y-3 rounded-xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Room types (required)</p>
                    <p className="text-xs text-slate-500">Add room categories because this listing is room-wise.</p>
                  </div>
                  <button
                    type="button"
                    onClick={addRoomTypeRow}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    + Add room type
                  </button>
                </div>
                {errors.room_types && <p className="text-xs text-rose-600">{errors.room_types}</p>}
                {roomTypes.length === 0 ? (
                  <p className="text-xs text-slate-500">No room types added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {roomTypes.map((room, idx) => (
                      <div key={room.id || `${room.name}-${idx}`} className="rounded-lg border border-slate-200 p-4">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-700">Room {idx + 1}</p>
                          <button
                            type="button"
                            onClick={() => removeRoomTypeRow(idx)}
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="mt-3 grid gap-4 md:grid-cols-2">
                          <Input
                            label="Room name"
                            required
                            value={room.name}
                            onChange={(e) => updateRoomType(idx, 'name', e.target.value)}
                          />
                          <Input
                            label="Bed type"
                            value={room.bed_type || ''}
                            onChange={(e) => updateRoomType(idx, 'bed_type', e.target.value)}
                          />
                          <Input
                            label="Capacity"
                            type="number"
                            min="1"
                            value={room.capacity}
                            onChange={(e) => updateRoomType(idx, 'capacity', e.target.value)}
                          />
                          <Input
                            label="Inventory (rooms)"
                            type="number"
                            min="1"
                            value={room.inventory}
                            onChange={(e) => updateRoomType(idx, 'inventory', e.target.value)}
                          />
                          <Input
                            label="Weekday price"
                            required
                            type="number"
                            min="0"
                            value={room.base_price_per_night}
                            onChange={(e) => updateRoomType(idx, 'base_price_per_night', e.target.value)}
                          />
                          <Input
                            label="Weekend price"
                            type="number"
                            min="0"
                            value={room.weekend_price_per_night}
                            onChange={(e) => updateRoomType(idx, 'weekend_price_per_night', e.target.value)}
                          />
                          <Input
                            label="Extra guest charge"
                            type="number"
                            min="0"
                            value={room.extra_guest_charge}
                            onChange={(e) => updateRoomType(idx, 'extra_guest_charge', e.target.value)}
                          />
                          <Select
                            label="Currency"
                            options={currencyOptions}
                            value={room.currency || form.currency}
                            onChange={(e) => updateRoomType(idx, 'currency', e.target.value)}
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={room.is_active !== false}
                              onChange={(e) => updateRoomType(idx, 'is_active', e.target.checked)}
                            />
                            <span className="text-sm text-slate-700">Active</span>
                          </div>
                        </div>
                        <div className="mt-3">
                          <TextArea
                            label="Room description"
                            rows={2}
                            value={room.description || ''}
                            onChange={(e) => updateRoomType(idx, 'description', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
                Entire property booking selected. Room-type selection will be hidden on the detail page.
              </div>
            )}
          </div>
      );
      case 'amenities':
        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="flex items-center gap-1 text-sm font-semibold text-slate-700">
                  <span>Amenities</span>
                  <span className="text-rose-600">*</span>
                </p>
                <p className="text-xs text-slate-500">Pick at least one amenity or add your own option.</p>
              </div>
              <button
                type="button"
                onClick={() => setAmenityDropdownOpen((open) => !open)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                {amenityDropdownOpen ? 'Hide list' : 'Select amenities'}
              </button>
            </div>
            <div className="space-y-3 rounded-xl border border-slate-200 p-4">
              {errors.amenities && <p className="text-xs text-rose-600">{errors.amenities}</p>}
              <div className="flex flex-wrap gap-2">
                {amenities.length === 0 ? (
                  <span className="text-xs text-slate-500">No amenities selected yet.</span>
                ) : (
                  amenities.map((key) => {
                    const label = allAmenityOptions.find((opt) => opt.key === key)?.label || key;
                    return (
                      <span
                        key={key}
                        className="flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"
                      >
                        {label}
                        <button
                          type="button"
                          onClick={() => toggleAmenity(key)}
                          className="text-emerald-700 hover:text-emerald-900"
                        >
                          x
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
              {amenityDropdownOpen && (
                <div className="space-y-3 border-t border-slate-100 pt-3">
                  <div className="grid max-h-56 grid-cols-1 gap-2 overflow-y-auto md:grid-cols-2">
                    {allAmenityOptions.map((am) => (
                      <label
                        key={am.key}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 hover:border-emerald-200"
                      >
                        <input
                          type="checkbox"
                          checked={amenities.includes(am.key)}
                          onChange={() => toggleAmenity(am.key)}
                        />
                        <span>{am.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="space-y-2 rounded-lg bg-slate-50 p-3">
                    <p className="text-xs font-semibold text-slate-700">Add a new amenity option</p>
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <input
                        type="text"
                        value={newAmenityOption}
                        onChange={(e) => {
                          setNewAmenityOption(e.target.value);
                          setAmenityOptionError('');
                        }}
                        placeholder="e.g., Heated pool"
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      />
                      <button
                        type="button"
                        onClick={addAmenityOption}
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                      >
                        + Add to dropdown
                      </button>
                    </div>
                    {amenityOptionError && <p className="text-xs text-rose-600">{amenityOptionError}</p>}
                  </div>
                </div>
              )}
            </div>
            <TextArea
              label="Custom amenities (comma separated)"
              value={form.amenities_custom}
              onChange={(e) => handleFieldChange('amenities_custom', e.target.value)}
              error={errors.amenities}
              helper="Example: Private chef, Heated pool"
            />
          </div>
        );
      case 'media':
        {
          const galleryWithIdx = images
            .map((img, idx) => ({ ...img, __index: idx }))
            .filter((img) => (img.url || '').trim());
          return (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Video link (YouTube / MP4)"
                  value={form.video_url}
                  onChange={(e) => handleFieldChange('video_url', e.target.value)}
                  error={errors.video_url}
                />
                <Input
                  label="Featured image (URL)"
                  value={form.featured_image}
                  onChange={(e) => handleFieldChange('featured_image', e.target.value)}
                  helper="Used as cover on cards"
                />
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <p className="flex items-center gap-1 text-sm font-semibold text-slate-900">
                    <span>Gallery uploader</span>
                    <span className="text-rose-600">*</span>
                  </p>
                  <p className="text-xs text-slate-600">
                    Uploads attach directly to this property. JPEG/PNG/WebP, max 8MB. Add at least 4 images.
                  </p>
                </div>

                <div
                  className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-600"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleLocalUpload(e.dataTransfer.files);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  role="presentation"
                >
                  <p className="text-sm font-semibold text-slate-900">Drop images here or click to select</p>
                  <p className="text-xs text-slate-500">Uploads stay on this page; no redirect.</p>
                  {uploadingImages ? (
                    <p className="text-xs text-emerald-700">{uploadingMessage || 'Uploading images...'}</p>
                  ) : (
                    <p className="text-xs text-slate-500">Select multiple images to upload together.</p>
                  )}
                  {uploadError && <p className="text-xs text-rose-600">{uploadError}</p>}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                  >
                    Select images
                  </button>
                  <span className="text-[11px] text-slate-500">Progress shows as you upload.</span>
                </div>

                <input
                  type="file"
                  multiple
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    const { files } = e.target;
                    if (files?.length) handleLocalUpload(files);
                    e.target.value = '';
                  }}
                />

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-800">Add image URLs (optional)</p>
                  <div className="flex flex-col gap-2 md:flex-row">
                    <textarea
                      value={bulkImagesText}
                      onChange={(e) => setBulkImagesText(e.target.value)}
                      rows={3}
                      placeholder="Paste multiple URLs separated by commas or new lines"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    />
                    <button
                      type="button"
                      onClick={addBulkImages}
                      className="h-fit rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900"
                    >
                      Load URLs
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500">Duplicates are ignored. URL upload stays on this page.</p>
                  {fileImportError && <p className="text-[11px] text-rose-600">{fileImportError}</p>}
                  <div className="space-y-1">
                    <p className="text-[11px] font-semibold text-slate-700">Import URLs from file (txt / csv)</p>
                    <input
                      type="file"
                      accept=".txt,.csv"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileImport(file);
                        e.target.value = '';
                      }}
                      className="text-xs"
                    />
                  </div>
                </div>

                {errors.images && <p className="text-xs text-rose-600">{errors.images}</p>}

                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">Gallery ({galleryWithIdx.length})</p>
                    <p className="text-xs text-slate-500">Set cover or featured without leaving this step.</p>
                  </div>
                  {galleryWithIdx.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                      No images yet. Upload to populate the gallery.
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {galleryWithIdx.map((img) => (
                        <div
                          key={img.url || img.__index}
                          className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                        >
                          <div className="h-36 w-full overflow-hidden rounded-md bg-slate-100">
                            {img.url ? (
                              <img
                                src={resolveImageUrl(img.url)}
                                alt={img.alt_text || img.alt || 'Gallery image'}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center text-xs text-slate-500">No preview</div>
                            )}
                          </div>
                          <input
                            type="text"
                            className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                            placeholder="Alt text"
                            value={img.alt_text || ''}
                            onChange={(e) => updateImage(img.__index, 'alt_text', e.target.value)}
                          />
                          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                            <button
                              type="button"
                              className={`rounded px-2 py-1 ${
                                img.is_cover ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                              }`}
                              onClick={() => updateImage(img.__index, 'is_cover', true)}
                              disabled={img.is_cover}
                            >
                              {img.is_cover ? 'Cover' : 'Set cover'}
                            </button>
                            <button
                              type="button"
                              className="rounded px-2 py-1 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleFieldChange('featured_image', img.url)}
                              disabled={!img.url}
                            >
                              Use as featured
                            </button>
                            <button
                              type="button"
                              className="rounded px-2 py-1 text-rose-600 hover:bg-rose-50"
                              onClick={() => removeImageRow(img.__index)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }
      case 'pricing':
        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Homevilas margin, GST, and checkout fees are controlled in Admin &gt; Pricing &amp; Deals &gt; Taxes &amp; Fees. Add only the host base pricing here.
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Base price / night (weekday)"
                required
                type="number"
                min="0"
                value={form.base_weekday_price}
                onChange={(e) => handleNumberField('base_weekday_price', e.target.value)}
                error={errors.base_weekday_price}
              />
              <Input
                label="Weekend price / night"
                required
                type="number"
                min="0"
                value={form.base_weekend_price}
                onChange={(e) => handleNumberField('base_weekend_price', e.target.value)}
                error={errors.base_weekend_price}
              />
              <Input
                label="Security deposit"
                type="number"
                min="0"
                value={form.security_deposit}
                onChange={(e) => handleNumberField('security_deposit', e.target.value)}
                error={errors.security_deposit}
              />
              <Input
                label="Taxes (%)"
                type="number"
                min="0"
                value={form.taxes_percent}
                onChange={(e) => handleNumberField('taxes_percent', e.target.value)}
                error={errors.taxes_percent}
              />
              <Input
                label="Extra guest charge"
                type="number"
                min="0"
                value={form.extra_guest_charge}
                onChange={(e) => handleNumberField('extra_guest_charge', e.target.value)}
                error={errors.extra_guest_charge}
              />
              <Input
                label="Cleaning fee"
                type="number"
                min="0"
                value={form.cleaning_fee}
                onChange={(e) => handleNumberField('cleaning_fee', e.target.value)}
                error={errors.cleaning_fee}
              />
              <Input
                label="Service fee"
                type="number"
                min="0"
                value={form.service_fee}
                onChange={(e) => handleNumberField('service_fee', e.target.value)}
                error={errors.service_fee}
              />
              <Select
                label="Currency"
                required
                value={form.currency}
                onChange={(e) => handleFieldChange('currency', e.target.value)}
                options={currencyOptions}
                error={errors.currency}
              />
              <Input
                label="Minimum stay (nights)"
                type="number"
                min="1"
                value={form.min_nights}
                onChange={(e) => handleNumberField('min_nights', e.target.value)}
                error={errors.min_nights}
              />
              <Input
                label="Maximum stay (nights)"
                type="number"
                min="1"
                value={form.max_nights}
                onChange={(e) => handleNumberField('max_nights', e.target.value)}
                error={errors.max_nights}
              />
              <Input
                label="Weekly discount (%)"
                type="number"
                min="0"
                value={form.weekly_discount}
                onChange={(e) => handleNumberField('weekly_discount', e.target.value)}
                error={errors.weekly_discount}
              />
              <Input
                label="Monthly discount (%)"
                type="number"
                min="0"
                value={form.monthly_discount}
                onChange={(e) => handleNumberField('monthly_discount', e.target.value)}
                error={errors.monthly_discount}
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Partial Payment</h3>
                  <p className="text-xs text-slate-500">
                    Keep this aligned with the admin policy for this property.
                  </p>
                </div>
                {partialPaymentLoading && (
                  <span className="text-xs font-semibold text-slate-500">Loading policy...</span>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-600">
                {partialPaymentPolicy?.effectiveConfig?.notes || 'Partial payment policy will load here.'}
              </div>

              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={!!partialPaymentForm.partialPaymentEnabled}
                  disabled={!partialPaymentHostCanEdit}
                  onChange={(e) =>
                    setPartialPaymentForm((prev) => ({
                      ...prev,
                      partialPaymentEnabled: e.target.checked,
                    }))
                  }
                />
                Partial payment enabled
              </label>

              <Input
                label="Partial payment percentage"
                type="number"
                min={partialPaymentRange.min}
                max={partialPaymentRange.max}
                value={partialPaymentForm.partialPaymentPercentage}
                onChange={(e) =>
                  setPartialPaymentForm((prev) => ({
                    ...prev,
                    partialPaymentPercentage: e.target.value,
                  }))
                }
                disabled={!partialPaymentHostCanEdit || !partialPaymentForm.partialPaymentEnabled}
                helper={`Allowed range: ${partialPaymentRange.min}% to ${partialPaymentRange.max}%`}
                error={errors.partial_payment}
              />

              <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                Guest can pay {partialPaymentForm.partialPaymentPercentage || 0}% now and remaining{' '}
                {Math.max(100 - Number(partialPaymentForm.partialPaymentPercentage || 0), 0)}% later.
              </div>

              {partialPaymentPreview && (
                <div className="rounded-lg border border-emerald-100 bg-white px-3 py-3 text-sm text-slate-700">
                  <p className="font-semibold text-slate-900">Preview on base weekday price</p>
                  <p className="mt-1">
                    Guest pays {form.currency} {partialPaymentPreview.amountPaidNow.toLocaleString()} now and {form.currency}{' '}
                    {partialPaymentPreview.remainingAmount.toLocaleString()} later.
                  </p>
                </div>
              )}

              {!partialPaymentHostCanEdit && (
                <p className="text-xs text-slate-500">
                  This setting is controlled by admin. You can view the rule here, but you cannot change it.
                </p>
              )}
            </div>
          </div>
        );
      case 'availability':
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Advance notice (hours)"
              type="number"
              min="0"
              value={form.cutoff_hours}
              onChange={(e) => handleNumberField('cutoff_hours', e.target.value)}
              error={errors.cutoff_hours}
            />
            <Input
              label="Booking window (days)"
              type="number"
              min="0"
              value={form.booking_window_days}
              onChange={(e) => handleNumberField('booking_window_days', e.target.value)}
              error={errors.booking_window_days}
              helper="How far ahead guests can book"
            />
            <Select
              label="Weekend pricing type"
              value={form.weekend_pricing_type}
              onChange={(e) => handleFieldChange('weekend_pricing_type', e.target.value)}
              options={[
                { value: 'percent', label: 'Markup %' },
                { value: 'fixed', label: 'Flat amount' },
              ]}
            />
            <Input
              label="Weekend pricing value"
              type="number"
              min="0"
              value={form.weekend_pricing_value}
              onChange={(e) => handleNumberField('weekend_pricing_value', e.target.value)}
              error={errors.weekend_pricing_value}
              helper="e.g., 20 for 20%"
            />
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={!!form.allow_same_day_booking}
                onChange={(e) => handleFieldChange('allow_same_day_booking', e.target.checked)}
              />
              Allow same-day booking
            </label>
          </div>
        );
      case 'policies': {
        const policyTabs = [
          { key: 'policies', label: 'Policies' },
          { key: 'house_rules', label: 'House Rules & Information' },
        ];

        const renderPolicyBlock = ({ label, required, value, onChange, error, helper, quickFillKey, fileKey, fileLabel }) => {
          const options = quickFillKey ? quickFillOptions[quickFillKey] || [] : [];
          const draft = quickFillKey ? quickFillDrafts[quickFillKey] || {} : {};
          return (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <span>{label}</span>
                  {required && <span className="text-rose-600">*</span>}
                </label>
                {quickFillKey && (
                  <div className="flex items-center gap-2">
                    {options.length > 0 && (
                      <select
                        className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        onChange={(e) => {
                          const preset = e.target.value;
                          if (preset) onChange(preset);
                          e.target.value = '';
                        }}
                        defaultValue=""
                      >
                        <option value="">Quick fill</option>
                        {options.map((opt) => (
                          <option key={opt.label} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => updateQuickFillDraft(quickFillKey, { open: true })}
                      className="text-xs font-semibold text-emerald-700 hover:underline"
                    >
                      + Add quick fill
                    </button>
                  </div>
                )}
              </div>
              <textarea
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  error ? 'border-rose-300' : 'border-slate-200'
                }`}
                rows={3}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={`Add ${label.toLowerCase()}`}
              />
              {quickFillKey && draft.open && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <div className="grid gap-2 md:grid-cols-2">
                    <input
                      className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      placeholder="Label"
                      value={draft.label || ''}
                      onChange={(e) => updateQuickFillDraft(quickFillKey, { label: e.target.value, error: '' })}
                    />
                    <input
                      className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      placeholder="Text"
                      value={draft.value || ''}
                      onChange={(e) => updateQuickFillDraft(quickFillKey, { value: e.target.value, error: '' })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => addQuickFillOption(quickFillKey)}
                      className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                    >
                      Save preset
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        updateQuickFillDraft(quickFillKey, { open: false, label: '', value: '', error: '' })
                      }
                      className="rounded border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    {draft.error && <p className="text-xs text-rose-600">{draft.error}</p>}
                  </div>
                </div>
              )}
              {helper && <p className="text-xs text-slate-500">{helper}</p>}
              {error && <p className="text-xs text-rose-600">{error}</p>}
              {fileKey && (
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <label className="cursor-pointer rounded border border-slate-200 px-3 py-1.5 font-semibold hover:bg-slate-50">
                    {fileLabel}
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileImport(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <span>Attach a doc/PDF instead of typing.</span>
                </div>
              )}
            </div>
          );
        };

        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                {policyTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setPoliciesTab(tab.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      policiesTab === tab.key
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => savePoliciesTab(policiesTab)}
                disabled={saving}
                className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
              >
                {saving ? 'Saving...' : policiesTab === 'policies' ? 'Save policies' : 'Save house rules'}
              </button>
            </div>
            {policiesTab === 'policies' ? (
              <div className="space-y-4">
                {renderPolicyBlock({
                  label: 'Cancellation policy',
                  required: true,
                  value: form.cancellation_policy,
                  onChange: (val) => handleFieldChange('cancellation_policy', val),
                  error: errors.cancellation_policy,
                  helper: 'Describe penalties, notice period, or link to a template.',
                  quickFillKey: 'cancellation_policy',
                  fileKey: 'cancellation_policy_doc',
                  fileLabel: 'Upload policy file',
                })}
                {renderPolicyBlock({
                  label: 'Refund rules',
                  value: form.refund_rules,
                  onChange: (val) => handleFieldChange('refund_rules', val),
                  error: errors.refund_rules,
                  quickFillKey: 'refund_rules',
                })}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {renderPolicyBlock({
                    label: 'Pet policy',
                    value: form.pet_policy,
                    onChange: (val) => handleFieldChange('pet_policy', val),
                    error: errors.pet_policy,
                    quickFillKey: 'pet_policy',
                  })}
                  {renderPolicyBlock({
                    label: 'Smoking & Alcohol Consumption Rules',
                    value: form.smoking_policy,
                    onChange: (val) => handleFieldChange('smoking_policy', val),
                    error: errors.smoking_policy,
                    quickFillKey: 'smoking_policy',
                  })}
                  {renderPolicyBlock({
                    label: 'Guest Profile',
                    value: form.guest_profile,
                    onChange: (val) => handleFieldChange('guest_profile', val),
                    error: errors.guest_profile,
                    quickFillKey: 'guest_profile',
                  })}
                  {renderPolicyBlock({
                    label: 'ID Proof Related',
                    value: form.id_requirement,
                    onChange: (val) => handleFieldChange('id_requirement', val),
                    error: errors.id_requirement,
                    quickFillKey: 'id_requirement',
                  })}
                </div>
                <div>
                  <p className="mb-2 flex items-center gap-1 text-sm font-semibold text-slate-800">
                    <span>House Rules & Information</span>
                    <span className="text-rose-600">*</span>
                  </p>
                  <p className="mb-3 text-xs text-slate-500">Add at least one house rule before final submit.</p>
                </div>
                {renderHouseRulesSectionsEditor(true)}
              </div>
            )}
          </div>
        );
      }
      case 'addons':
        return (
          <div className="space-y-3">
            {addons.map((addon, idx) => (
              <div key={idx} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-800">Add-on {idx + 1}</p>
                  <button
                    type="button"
                    onClick={() => removeAddonRow(idx)}
                    className="text-xs font-semibold text-rose-600 hover:underline"
                    disabled={addons.length === 1}
                  >
                    Remove
                  </button>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-4">
                  <Input
                    label="Name"
                    value={addon.name}
                    onChange={(e) => updateAddon(idx, 'name', e.target.value)}
                  />
                  <Input
                    label="Description"
                    value={addon.description}
                    onChange={(e) => updateAddon(idx, 'description', e.target.value)}
                  />
                  <Input
                    label="Price"
                    type="number"
                    min="0"
                    value={addon.price}
                    onChange={(e) => updateAddon(idx, 'price', e.target.value)}
                    error={errors[`addons-${idx}`]}
                  />
                  <Select
                    label="Per"
                    value={addon.per}
                    onChange={(e) => updateAddon(idx, 'per', e.target.value)}
                    options={[
                      { value: 'booking', label: 'Per booking' },
                      { value: 'night', label: 'Per night' },
                    ]}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addAddonRow}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
            >
              + Add more
            </button>
          </div>
        );
      case 'seo':
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Meta title"
              required
              value={form.meta_title}
              onChange={(e) => handleFieldChange('meta_title', e.target.value)}
              helper="Required for final submit"
              error={errors.meta_title}
            />
            <Input
              label="Meta description"
              required
              value={form.meta_description}
              onChange={(e) => handleFieldChange('meta_description', e.target.value)}
              helper="Required for final submit"
              error={errors.meta_description}
            />
            <Input
              label="URL slug"
              value={form.slug}
              onChange={(e) => handleFieldChange('slug', e.target.value)}
              helper="Optional: override default URL"
              error={errors.slug}
            />
            <div className="space-y-1 text-sm text-slate-700">
              <div className="flex items-center gap-2 font-semibold">
                <span>Status</span>
                <span className="text-xs font-semibold text-emerald-600">Draft until you publish</span>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {statusDisplay}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const statusDisplay =
    form.status === 'published'
      ? 'Published'
      : form.status === 'suspended'
      ? 'Suspended'
      : form.status === 'pending'
      ? 'Pending approval'
      : 'Draft';

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600">Loading property...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Homevilas</p>
          <h1 className="text-2xl font-bold text-slate-900">{isEdit ? 'Edit Property' : 'Add Property'}</h1>
          <p className="text-sm text-slate-600">All steps available below</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            {statusDisplay}
            {statusDisplay === 'Draft' ? ' (Draft until submit)' : ''}
          </span>
          <button
            type="button"
            onClick={() => navigate('/host/properties')}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {steps.map((step, idx) => (
          <StepPill
            key={step.key}
            index={idx}
            label={step.label}
            active={idx === currentStep}
            complete={stepCompletion[idx]}
            onClick={(nextIdx) => {
              if (nextIdx <= currentStep) {
                setCurrentStep(nextIdx);
              } else {
                const stepErrors = validateStep(
                  steps[currentStep].key,
                  form,
                  images,
                  amenities,
                  addons,
                  false,
                  selectedCountryId,
                  selectedStateId,
                  selectedCityId,
                  roomTypes
                );
                if (Object.keys(stepErrors).length) {
                  setErrors((prev) => ({ ...prev, ...stepErrors }));
                  return;
                }
                setCurrentStep(nextIdx);
              }
            }}
          />
        ))}
      </div>

      {errors.form && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">{errors.form}</div>
      )}
      {statusMessage && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">{statusMessage}</div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">{stepContent()}</div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 0}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
        >
          Back
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          {currentStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              disabled={saving}
            >
              Save & Next
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleSave(true)}
              disabled={saving}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? 'Submitting...' : 'Submit property'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HostPropertyForm;


